import { useLocation } from "wouter";
import { FAQ_SECTIONS } from "./faqContent";
import { RESOURCE_GUIDE_BY_PATH } from "./resourceGuides";
import { EVALUATION_FAQS } from "./evaluationContent";
import metadata from "./siteMetadata.json";

const ORIGIN = "https://envelaro.com";
const ORGANIZATION_ID = `${ORIGIN}/#organization`;
const WEBSITE_ID = `${ORIGIN}/#website`;

const routeLabels: Record<string, string> = {
  about: "About",
  contact: "Contact",
  evaluation: "Evaluation",
  migration: "Migration",
  resources: "Resources",
  tools: "Free tools",
  cpanel: "cPanel",
  godaddy: "GoDaddy",
  "google-workspace": "Google Workspace",
  "microsoft-365": "Microsoft 365",
  "zoho-mail": "Zoho Mail",
  "dkim-guide": "DKIM guide",
  "dmarc-guide": "DMARC guide",
  "email-deliverability-guide": "Email deliverability guide",
  "business-email-provider-comparison": "Business email provider comparison",
};

const servicePaths = new Set([
  "/product",
  "/small-business-email-hosting",
  "/custom-domain-email-hosting",
  "/multiple-domain-email-hosting",
  "/managed-email-setup",
  "/managed-private-deployment",
  "/migration",
  "/migration/cpanel",
  "/migration/godaddy",
  "/migration/google-workspace",
  "/migration/microsoft-365",
  "/migration/zoho-mail",
]);

function normalizedPath(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function breadcrumbList(path: string, title: string) {
  const segments = path.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${ORIGIN}/`,
    },
  ];

  segments.forEach((segment, index) => {
    const itemPath = `/${segments.slice(0, index + 1).join("/")}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: index === segments.length - 1
        ? title.replace(/\s*\|\s*Envelaro.*$/, "")
        : routeLabels[segment] ?? segment.replaceAll("-", " "),
      item: `${ORIGIN}${itemPath}`,
    });
  });

  return {
    "@type": "BreadcrumbList",
    "@id": `${ORIGIN}${path === "/" ? "/" : path}#breadcrumbs`,
    itemListElement: items,
  };
}

export function structuredDataForPath(pathname: string) {
  const path = normalizedPath(pathname);
  const routeMetadata = (metadata as Record<string, { title: string; description: string }>)[path];
  if (!routeMetadata) return null;

  const url = `${ORIGIN}${path === "/" ? "/" : path}`;
  const organization = {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: "Envelaro",
    url: `${ORIGIN}/`,
    email: "support@envelaro.com",
    description: "Envelaro provides managed custom-domain business email, webmail, administration, migration guidance and connected business communication tools.",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@envelaro.com",
      availableLanguage: "English",
      url: `${ORIGIN}/contact`,
    },
    knowsAbout: [
      "Business email hosting",
      "Custom-domain email",
      "Email migration",
      "SPF",
      "DKIM",
      "DMARC",
      "Email deliverability",
    ],
  };
  const website = {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    name: "Envelaro",
    url: `${ORIGIN}/`,
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en",
  };
  const breadcrumbs = breadcrumbList(path, routeMetadata.title);
  const webpage = {
    "@type": path === "/resources" ? "CollectionPage" : "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: routeMetadata.title,
    description: routeMetadata.description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": ORGANIZATION_ID },
    breadcrumb: { "@id": breadcrumbs["@id"] },
    inLanguage: "en",
  };
  const graph: Record<string, unknown>[] = [organization, website, webpage, breadcrumbs];

  if (servicePaths.has(path)) {
    graph.push({
      "@type": "Service",
      "@id": `${url}#service`,
      name: routeMetadata.title.replace(/\s*\|\s*Envelaro.*$/, ""),
      description: routeMetadata.description,
      url,
      provider: { "@id": ORGANIZATION_ID },
      audience: {
        "@type": "BusinessAudience",
        audienceType: "Organizations using domains they own or are authorized to administer",
      },
    });
  }

  if (path === "/faq") {
    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: FAQ_SECTIONS.flatMap((section) =>
        section.questions.map(([question, answer]) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        })),
      ),
    });
  }

  if (path === "/evaluation") {
    graph.push({
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      mainEntity: EVALUATION_FAQS.map(({ question, answer }) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      })),
    });
  }

  const guide = RESOURCE_GUIDE_BY_PATH.get(path);
  if (guide) {
    graph.push({
      "@type": "TechArticle",
      "@id": `${url}#article`,
      headline: guide.title,
      description: guide.description,
      mainEntityOfPage: { "@id": `${url}#webpage` },
      author: { "@id": ORGANIZATION_ID },
      publisher: { "@id": ORGANIZATION_ID },
      datePublished: guide.publishedAt ?? "2026-08-28",
      dateModified: guide.updatedAt ?? guide.publishedAt ?? "2026-08-28",
      inLanguage: "en",
      citation: guide.sources.map((source) => source.url),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}

export function StructuredData() {
  const [location] = useLocation();
  const data = structuredDataForPath(location);
  if (!data) return null;

  return (
    <script
      data-envelaro-structured-data="true"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      type="application/ld+json"
    />
  );
}
